import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { getDataFromAPI } from "../../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import { useNavigate, useParams } from "react-router-dom";
import BPUserDetailsComponent from "./component";

const BPUserDetailsContainer = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [BPList, setBPList] = useState([]);
  const [filteredBPList, setFilteredBPList] = useState([]);
  const [corporateName, setCorporateName] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [editedDepartmentName, setEditedDepartmentName] = useState("");
  const [currentStatusFilter, setCurrentStatusFilter] = useState("active");
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  useEffect(() => {
    if (id) {
      handleGetCorporateSeries();
    }
  }, [id]);

  useEffect(() => {
    filterBPList();
  }, [BPList, searchValue, currentStatusFilter]);

  const handleGetCorporateSeries = (offsetValue = 0, append = false) => {
    if (loading) return;
    setLoading(true);
    setInitialLoading(true);

    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}?limit=${limit}&offset=${offsetValue}`,
      "GET",
      undefined,
      (response) => {
        console.log("API Response:", response);
        
        // Set corporate name from response
        if (response.corporateName) {
          setCorporateName(response.corporateName);
        }
        
        // Get the user list
        const newList = response.bpUserInfoList || [];
        
        if (append) {
          setBPList(prev => [...prev, ...newList]);
        } else {
          setBPList(newList);
        }
        
        // Check if more data is available
        setHasMore(newList.length === limit);
        setOffset(offsetValue + 1);
        setLoading(false);
        setInitialLoading(false);
      },
      (err) => {
        console.error("API Error:", err);
        toastr.error("Failed", "Unable to fetch business profiles.");
        setLoading(false);
        setInitialLoading(false);
      }
    );
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    const bottom = scrollHeight - scrollTop <= clientHeight + 50;
    
    if (bottom && hasMore && !loading) {
      handleGetCorporateSeries(offset, true);
    }
  };

  const getSelectedEmployeeIds = () => {
    return selectedRows
      .map((index) => filteredBPList[index]?.usersCorporateId)
      .filter(Boolean);
  };

  const handleBulkActivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No employee IDs found for selected rows.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}/activate`,
      "POST",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", "Users activated successfully.");
        handleGetCorporateSeries(0, false);
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        console.error("Activation Error:", err);
        toastr.error("Error", err?.message || "Failed to activate users.");
        setLoading(false);
      }
    );
  };

  const handleBulkDeactivate = () => {
    const selectedEmployeeIds = getSelectedEmployeeIds();
    if (selectedEmployeeIds.length === 0) {
      toastr.error("Error", "No employee IDs found for selected rows.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${id}`,
      "DELETE",
      { employeeIds: selectedEmployeeIds },
      () => {
        toastr.success("Success", "Users deactivated successfully.");
        handleGetCorporateSeries(0, false);
        setSelectedRows([]);
        setLoading(false);
      },
      (err) => {
        console.error("Deactivation Error:", err);
        toastr.error("Failed", err?.message || "Unable to deactivate selected users.");
        setLoading(false);
      }
    );
  };

  const handleUpdateUser = (ucmId) => {
    if (!ucmId) {
      toastr.error("Error", "No user ID provided");
      return;
    }
    
    const userToUpdate = filteredBPList[currentRowIndex];
    setLoading(true);
    
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/${ucmId}`,
      "PATCH",
      {
        corporateId: parseInt(id),
        userName: editedName,
        email: userToUpdate.emailId,
        businessName: editedDepartmentName
      },
      () => {
        toastr.success("Success", "User updated successfully");
        handleGetCorporateSeries(0, false);
        setEditOpen(false);
        setLoading(false);
      },
      (err) => {
        console.error("Update Error:", err);
        toastr.error("Error", err?.message || "Failed to update user.");
        setLoading(false);
      }
    );
  };

  const filterBPList = () => {
    let filtered = [...BPList];
    
    // Filter by status
    if (currentStatusFilter === "active") {
      filtered = filtered.filter((user) => user.status === true);
    } else if (currentStatusFilter === "inactive") {
      filtered = filtered.filter((user) => user.status === false);
    }
    
    // Filter by search
    if (searchValue.trim()) {
      const searchLower = searchValue.toLowerCase().trim();
      filtered = filtered.filter(
        (user) =>
          (user.usersCorporateId?.toString().toLowerCase().includes(searchLower)) ||
          (user.userName?.toLowerCase().includes(searchLower)) ||
          (user.emailId?.toLowerCase().includes(searchLower))
      );
    }
    
    setFilteredBPList(filtered);
    // Reset selected rows when filter changes
    setSelectedRows([]);
  };

  const handleSearchChange = (e) => setSearchValue(e.target.value);
  
  const handleStatusToggle = (status) => {
    setCurrentStatusFilter(status);
    setSelectedRows([]);
  };
  
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(filteredBPList.map((_, i) => i));
    } else {
      setSelectedRows([]);
    }
  };
  
  const handleCheckboxChange = (index) => {
    setSelectedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };
  
  const handleEditClick = (index) => {
    setCurrentRowIndex(index);
    const user = filteredBPList[index];
    setEditedName(user.userName || '');
    setEditedDepartmentName(user.businessName || user.departmentName || '');
    setEditOpen(true);
  };
  
  const handleSaveEdit = () => {
    if (currentRowIndex === null) return;
    const userToUpdate = filteredBPList[currentRowIndex];
    handleUpdateUser(userToUpdate.ucmId);
  };
  
  const handleUnlinkClick = (index) => {
    setCurrentRowIndex(index);
    setUnlinkOpen(true);
  };
  
  const handleConfirmUnlink = () => {
    const userToUnlink = filteredBPList[currentRowIndex];
    const userUcmId = userToUnlink?.ucmId;
    
    if (!userUcmId) {
      toastr.error("Error", "No user ID found to unlink.");
      return;
    }
    
    setLoading(true);
    props.getDataFromAPI(
      `/partner/api/v1/business-profile/approve/${userUcmId}`,
      "POST",
      undefined,
      () => {
        toastr.success("Success", "User unlinked successfully.");
        handleGetCorporateSeries(0, false);
        setUnlinkOpen(false);
        setLoading(false);
      },
      (err) => {
        console.error("Unlink Error:", err);
        toastr.error("Error", err?.message || "Failed to unlink user.");
        setLoading(false);
      }
    );
  };
  
  const handleAddNewUser = () => {
    navigate(`/home/customermanager/bpmanagementlist/bpmanagementusercreation/${id}`);
  };

  return (
    <BPUserDetailsComponent
      BPList={filteredBPList}
      corporateName={corporateName}
      selectedRows={selectedRows}
      handleSelectAll={handleSelectAll}
      handleCheckboxChange={handleCheckboxChange}
      editOpen={editOpen}
      setEditOpen={setEditOpen}
      editedName={editedName}
      setEditedName={setEditedName}
      editedDepartmentName={editedDepartmentName}
      setEditedDepartmentName={setEditedDepartmentName}
      handleEditClick={handleEditClick}
      handleSaveEdit={handleSaveEdit}
      unlinkOpen={unlinkOpen}
      setUnlinkOpen={setUnlinkOpen}
      handleUnlinkClick={handleUnlinkClick}
      handleConfirmUnlink={handleConfirmUnlink}
      searchValue={searchValue}
      handleSearchChange={handleSearchChange}
      handleStatusToggle={handleStatusToggle}
      currentStatusFilter={currentStatusFilter}
      handleAddNewUser={handleAddNewUser}
      handleBulkActivate={handleBulkActivate}
      handleBulkDeactivate={handleBulkDeactivate}
      handleScroll={handleScroll}
      loading={loading || initialLoading}
      totalCount={BPList.length}
    />
  );
};

export default connect(null, { getDataFromAPI })(BPUserDetailsContainer);